import React, { useEffect } from "react";
import { Meta } from "../layout/Meta";
import { SITE } from "../config/site";
import Link from "../components/Link";
import Eyebrow from "../components/ui/Eyebrow";
import { PRODUCT_ASSETS } from "../content/productAssets";
import { productJsonLd, scrollToHash } from "../lib/productSeo";
import {
  ProductSection,
  ProductBreadcrumb,
  DisplayHeading,
  ProductArtwork,
  FeatureCard,
  InkAppCta,
  TradeTimeline,
  IllustrativeNote,
} from "../components/product";

const PATH = "/bot-trader";
const TITLE = "Bot Trader";
const DESCRIPTION =
  "Follow a Stratify paper trade from signal to simulated fill. Review the time between, fill details, and notification history in Bot Trader.";

const hero = PRODUCT_ASSETS.botTraderHero;
const tradeCard = PRODUCT_ASSETS.botTraderTradeCard;
const notifications = PRODUCT_ASSETS.botTraderNotifications;

export default function BotTraderPage() {
  useEffect(() => {
    scrollToHash();
  }, []);

  return (
    <div className="w-full bg-bg0">
      <Meta
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        keywords={[
          "Bot Trader",
          "paper trading",
          "paper fill",
          "signal to fill",
          "trading alerts",
          "Stratify Backtesting",
        ]}
        jsonLd={productJsonLd({ title: TITLE, description: DESCRIPTION, path: PATH })}
      />

      <ProductSection className="overflow-hidden pt-10 md:pt-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-24 -z-10 mx-auto h-64 max-w-5xl opacity-40"
        >
          <img src="/images/hero-wave.png" alt="" className="h-full w-full object-contain" />
        </div>

        <ProductBreadcrumb current="Bot Trader" />

        <div className="mt-8 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <DisplayHeading>
              From signal
              <br />
              to fill.
            </DisplayHeading>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
              See the signal, the paper fill and the time between.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
              <InkAppCta>Get the app</InkAppCta>
              <Link
                to="#trade-sequence"
                className="inline-flex min-h-11 items-center text-sm font-medium text-ink/80 transition hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                Explore how it works
                <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
          <div className="min-w-0 lg:col-span-7">
            <ProductArtwork
              src={hero.src}
              width={hero.width}
              height={hero.height}
              alt={hero.alt}
              priority
            />
          </div>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-3">
          <FeatureCard
            icon="bolt"
            title="Signal time"
            body="See exactly when the bot spots an opportunity."
            label="Precise timing"
          />
          <FeatureCard
            icon="document"
            title="Fill details"
            body="View the paper fill time, price and size for every trade."
            label="Complete context"
          />
          <FeatureCard
            icon="clock"
            title="Recorded latency"
            body="Know the time between signal and fill, in seconds."
            label="Measure what matters"
          />
        </div>
      </ProductSection>

      <ProductSection id="trade-sequence">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow dot={false}>Bot activity</Eyebrow>
            <DisplayHeading as="h2" className="mt-4">
              Open the trade.
              <br />
              Understand the sequence.
            </DisplayHeading>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted">
              See exactly what happened after a signal — from detection to paper fill.
              Every event is time-stamped so you can review latency, execution and outcome.
            </p>
            <div className="mt-8">
              <TradeTimeline
                events={[
                  {
                    time: "1:50pm",
                    title: "Signal detected",
                    body: "The saved rules identified an opportunity and sent the signal to the bot.",
                  },
                  {
                    time: "1:51pm",
                    title: "Order filled",
                    body: "The bot recorded a paper fill so you can review the simulated position.",
                    active: true,
                  },
                  {
                    time: "1:56pm",
                    title: "Monitoring",
                    body: "You continue to track the paper position and manage exit logic.",
                  },
                ]}
              />
            </div>
            <IllustrativeNote className="mt-6">
              Example times and latency are illustrative. They are not live website data or a
              guaranteed execution speed.
            </IllustrativeNote>
          </div>
          <div className="min-w-0 lg:col-span-7">
            <ProductArtwork
              src={tradeCard.src}
              width={tradeCard.width}
              height={tradeCard.height}
              alt={tradeCard.alt}
              className="mx-auto md:max-w-xl lg:max-w-none"
            />
          </div>
        </div>
      </ProductSection>

      <ProductSection id="notifications">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <DisplayHeading as="h2">
              Stay close
              <br />
              to every trade.
            </DisplayHeading>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
              Review paper fill alerts and your notification history.
            </p>
            <div className="mt-8">
              <InkAppCta>Get the app</InkAppCta>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              <FeatureCard
                icon="document"
                title="Fill details"
                body="See the share quantity, price and fill latency for every execution."
              />
              <FeatureCard
                icon="tag"
                title="Paper labels"
                body="All simulated trades are clearly labeled paper."
              />
              <FeatureCard
                icon="bell"
                title="Notification history"
                body="Review your recent alerts anytime in the app."
              />
            </div>
          </div>
          <div className="min-w-0 lg:col-span-6">
            <ProductArtwork
              src={notifications.src}
              width={notifications.width}
              height={notifications.height}
              alt={notifications.alt}
              className="mx-auto max-w-md lg:max-w-lg"
            />
          </div>
        </div>
        <p className="mt-10 text-xs text-muted">
          Stratify is a research and simulation tool. Not financial advice.{" "}
          <a
            href={SITE.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-hint-deep underline-offset-4 hover:underline"
          >
            Download on the App Store
          </a>
          .
        </p>
      </ProductSection>
    </div>
  );
}
